import {
  CarouselLayout,
  DisconnectButton,
  FocusLayout,
  GridLayout,
  LiveKitRoom,
  ParticipantTile,
  RoomAudioRenderer,
  TrackToggle,
  useLocalParticipant,
  useParticipants,
  useRemoteParticipants,
  useRoomContext,
  useTracks,
  type TrackReferenceOrPlaceholder,
} from '@livekit/components-react';
import '@livekit/components-styles';
import {
  RoomEvent,
  ScreenSharePresets,
  Track,
  type RemoteParticipant,
  type RoomOptions,
  type ScreenShareCaptureOptions,
} from 'livekit-client';
import { useEffect, useRef, useState, type ReactElement } from 'react';

import { ApiError, apiFetch } from '../lib/api';
import { screenAudioAdvice } from '../lib/browserCapabilities';
import { isDesktopShell, subscribeToPushToTalk } from '../lib/desktopShell';
import { IDLE_PUSH_TO_TALK, nextPushToTalk, type PushToTalkState } from '../lib/pushToTalk';
import {
  playVoiceChime,
  readChimePreference,
  shouldPlayChime,
  writeChimePreference,
  type VoiceChimeKind,
} from '../lib/voiceChime';
import { AudioSettings } from './AudioSettings';

/**
 * A voice channel: audio, camera and screen share, all carried by LiveKit.
 *
 * The backend never touches media. Its whole part is the six-hour JWT this
 * component asks for, and from there the browser talks straight to the SFU —
 * which is why there is no WebRTC code here: the toggle that opens the window
 * picker, the video grid and the speaking indicator all ship with
 * `@livekit/components-react`.
 *
 * Two decisions are ours and are made below. A shared screen takes the whole
 * stage and pushes everyone else into a strip, because in a room that is
 * watching a screen the screen *is* the conversation. And `adaptiveStream`
 * plus `dynacast` are on, which is what keeps the SFU sending each subscriber
 * only the quality layer their tile is actually rendered at — the bandwidth
 * budget of §12 of the design assumes both.
 */

// ---------------------------------------------------------------------------
// Room configuration
// ---------------------------------------------------------------------------

/**
 * `adaptiveStream` pauses and downgrades tracks by the size they are rendered
 * at; `dynacast` stops publishing layers nobody is subscribed to. Turning
 * either off multiplies the egress of a 15-person room several times over.
 */
const ROOM_OPTIONS: RoomOptions = {
  adaptiveStream: true,
  dynacast: true,

  /**
   * Audio capture is pinned rather than left to the browser's defaults.
   *
   * `red` off is the load-bearing one. LiveKit turns on RED (RFC 2198 audio
   * redundancy) by default on Chrome, and a subscriber whose browser will not
   * decode it receives the track and hears silence — a failure that looks
   * exactly like a broken microphone from both ends. Plain Opus costs a little
   * resilience to packet loss and is understood everywhere.
   *
   * The three processing flags are set explicitly because "unset" does not mean
   * "on": leaving echo cancellation to chance is how a room fills with feedback
   * the moment somebody uses speakers instead of headphones.
   */
  publishDefaults: {
    red: false,
    dtx: true,

    /**
     * Audio is what a voice channel is for, so it is told to survive first.
     *
     * When a link congests the browser has to choose what to starve, and the
     * default treats both streams alike. Marking audio `high` and video `low`
     * makes the video degrade while speech keeps its packets — which matters
     * most for whoever is furthest away, since their path is the one that
     * runs out of room first.
     */
    audioPreset: { maxBitrate: 24_000 },

    /**
     * When the link runs short, give up sharpness rather than movement.
     *
     * The default holds the resolution and drops frames, which is the
     * "it froze" that got reported: the picture stops while the encoder waits
     * for room it never gets. Trading pixels for framerate keeps a shared
     * screen legible and moving on a link that cannot carry it at full size.
     */
    degradationPreference: 'maintain-framerate',
  },
  audioCaptureDefaults: {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
  },
};

/**
 * Tracks the stage renders: real pictures only.
 *
 * Neither source carries a placeholder, so somebody with the camera off takes
 * no room on the stage at all. In a channel built around watching a screen an
 * avatar tile is dead weight — it competes with the picture for the space that
 * matters. Who is connected and who is talking moves to the roster strip
 * below, which costs one line instead of a grid cell.
 */
const STAGE_SOURCES = [
  { source: Track.Source.ScreenShare, withPlaceholder: false },
  { source: Track.Source.Camera, withPlaceholder: false },
];

/**
 * Screen share capture options.
 *
 * `audio: true` only makes the browser *offer* the sound: Chrome then shows
 * the "share tab/system audio" checkbox in its own picker and the person
 * decides there. With `audio: false` the checkbox never appears, so the toggle
 * below is the way to rule the sound out before the picker opens.
 *
 * `suppressLocalAudioPlayback` stops the shared tab from also playing out of
 * the sharer's own speakers, which otherwise arrives twice — once locally and
 * once back through the room. It is a Chrome constraint that neither
 * `lib.dom` nor LiveKit's types know about yet, hence the local declaration:
 * the alternative is casting the whole object and losing every other check.
 *
 * It does NOT prevent the loop that whole-screen sharing creates. Sharing the
 * entire screen with system audio captures the output mix, and the other
 * people's voices are in that mix — headphones do not help, because the
 * capture is a digital loopback of what the OS renders, not a microphone
 * picking up the room. Only sharing a single tab isolates the sound, which is
 * what the hint below the toggle tells the user.
 */
/**
 * Quality choices offered before the picker opens.
 *
 * 720p at 30fps is the default, not 1080p, and the reason is measured rather
 * than assumed: sharing a 1080p video produced 19 keyframe requests in forty
 * minutes on the other side — packet loss resetting the decoder, which is
 * exactly what "it froze" looks like. The server was idle at 1.85% CPU
 * throughout, so the ceiling was the link, not the machine.
 *
 * `contentHint: 'motion'` is the other half. It tells the encoder to spend its
 * budget on framerate instead of sharpness, which is what watching a video
 * together needs; the default setting optimises for still text and stutters on
 * movement.
 */
export const SCREEN_SHARE_QUALITIES = {
  suave: { rotulo: 'Suave (720p)', preset: ScreenSharePresets.h720fps30 },
  nitido: { rotulo: 'Nítido (1080p)', preset: ScreenSharePresets.h1080fps30 },
} as const;

export type ScreenShareQuality = keyof typeof SCREEN_SHARE_QUALITIES;

export function screenShareOptions(
  quality: ScreenShareQuality,
  withAudio: boolean,
): ScreenShareCaptureOptions {
  const { preset } = SCREEN_SHARE_QUALITIES[quality];

  return {
    audio: withAudio,
    suppressLocalAudioPlayback: withAudio,
    contentHint: 'motion',
    resolution: preset.resolution,
  };
}

const GENERIC_FAILURE = 'Não foi possível entrar no canal de voz. Tente novamente.';

// ---------------------------------------------------------------------------
// Stage
// ---------------------------------------------------------------------------

export interface StageSplit {
  /** The track that gets the large slot, or `null` when nobody is sharing. */
  focus: TrackReferenceOrPlaceholder | null;
  /** Everyone else, in the order the room reported them. */
  rest: TrackReferenceOrPlaceholder[];
}

/**
 * Decides who gets the big slot: the first shared screen, if there is one.
 *
 * A second screen is not promoted — it stays in the strip and can be watched
 * there, which is simpler and more predictable than a stage that reshuffles
 * every time someone else starts sharing.
 */
export function splitStage(tracks: TrackReferenceOrPlaceholder[]): StageSplit {
  const index = tracks.findIndex((item) => item.source === Track.Source.ScreenShare);

  if (index === -1) {
    return { focus: null, rest: tracks };
  }

  return {
    focus: tracks[index] ?? null,
    rest: tracks.filter((_, position) => position !== index),
  };
}

export interface StageView {
  /** The track blown up to fill the box, or `null` while nothing is. */
  focused: TrackReferenceOrPlaceholder | null;
  /** Everything else, as tiles — screens and cameras alike. */
  tiles: TrackReferenceOrPlaceholder[];
}

/** Identifies a track across renders; publications are recreated, sids are not. */
export function trackKey(track: TrackReferenceOrPlaceholder): string {
  return `${track.participant.identity}:${track.source}`;
}

/**
 * What fills the box and what sits in the strip.
 *
 * A shared screen is a tile like any other until somebody asks for it. Blowing
 * one up the moment it starts takes the room over for everyone at once, with
 * no way back — and the tile is not a placeholder: it carries the live
 * picture, small, which is how you see there is something worth opening. That
 * it is small is also what keeps it cheap, since `adaptiveStream` sends each
 * subscriber the layer their tile is actually drawn at.
 *
 * A focus that names a track no longer in the room resolves to `null` rather
 * than an empty box: the person who was sharing stopped, and the room should
 * fall back to the grid instead of holding a frame that will never update.
 */
interface VoiceStageProps {
  /** Which track fills the box, or `null` for the grid. */
  focusedKey: string | null;
  /** Asks for a track to fill the box, or `null` to go back to the grid. */
  onFocus: (key: string | null) => void;
}

export function stageView(
  tracks: TrackReferenceOrPlaceholder[],
  focusedKey: string | null,
): StageView {
  const focused = tracks.find((track) => trackKey(track) === focusedKey) ?? null;

  return {
    focused,
    tiles: focused === null ? tracks : tracks.filter((track) => track !== focused),
  };
}

/**
 * The height lives on this wrapper rather than on the LiveKit layout itself:
 * `lk-grid-layout` and `lk-focus-layout` both set their own height, and the
 * library stylesheet is loaded after ours, so a rule of equal specificity of
 * ours would lose. Sizing the box around them sidesteps the fight entirely.
 */
function VoiceStage({ focusedKey, onFocus }: VoiceStageProps): ReactElement {
  const tracks = useTracks(STAGE_SOURCES);
  const { focused, tiles } = stageView(tracks, focusedKey);

  // Nobody has a camera or a screen on: say so instead of rendering an empty
  // grid, which reads as something failing to load.
  if (tracks.length === 0) {
    return (
      <div className="voice-stage voice-stage-empty">
        <p className="voice-stage-hint">
          Ninguém está transmitindo. Use <strong>Compartilhar tela</strong> ou{' '}
          <strong>Câmera</strong> para começar.
        </p>
      </div>
    );
  }

  if (focused !== null) {
    return (
      <div className="voice-stage">
        <div className="voice-focus">
          <button type="button" className="voice-focus-exit" onClick={() => onFocus(null)}>
            ✕ Parar de assistir
          </button>
          <FocusLayout trackRef={focused} />
        </div>

        {tiles.length > 0 && (
          <CarouselLayout tracks={tiles}>
            <ParticipantTile />
          </CarouselLayout>
        )}
      </div>
    );
  }

  return (
    <div className="voice-stage">
      <GridLayout tracks={tiles}>
        <ParticipantTile />
      </GridLayout>

      {/*
        The overlay is what makes a tile openable without wrapping
        `ParticipantTile`, which LiveKit clones per track and would fight a
        wrapper. One button per tile, positioned over it by the grid.
      */}
      <div className="voice-stage-openers" aria-hidden="false">
        {tiles.map((track) => (
          <button
            key={trackKey(track)}
            type="button"
            className="voice-stage-open"
            onClick={() => onFocus(trackKey(track))}
          >
            {track.source === Track.Source.ScreenShare
              ? `Assistir a tela de ${track.participant.name || track.participant.identity}`
              : `Ampliar a câmera de ${track.participant.name || track.participant.identity}`}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Warns when a screen is being shared without its sound.
 *
 * `audio: true` only makes Chrome *offer* the checkbox in its own picker;
 * nobody ticking it produces a share with no audio track at all, and the app
 * happily carries on looking like it is sending sound. That silent failure is
 * what this catches — it compares what the person asked for against what the
 * browser actually handed over. Sharing a single window is the trap: Chrome
 * offers no audio checkbox for it whatsoever.
 */
function ScreenAudioWarning({ wanted }: { wanted: boolean }): ReactElement | null {
  const { localParticipant } = useLocalParticipant();

  const advice = screenAudioAdvice({
    wanted,
    sharing: localParticipant.isScreenShareEnabled,
    hasAudioTrack:
      localParticipant.getTrackPublication(Track.Source.ScreenShareAudio) !== undefined,
    desktop: isDesktopShell(),
  });

  if (!advice.show) {
    return null;
  }

  return (
    <p className="voice-share-warning" role="alert">
      {advice.message}
    </p>
  );
}

/**
 * One line naming everyone connected, with a dot on whoever is talking.
 *
 * This is what replaces the avatar tiles: the same two facts — who is here and
 * who is speaking — for a line of height instead of a grid cell each.
 */
function VoiceRoster({ onFocus }: { onFocus: (key: string | null) => void }): ReactElement {
  const participants = useParticipants();

  return (
    <ul className="voice-roster" aria-label="Participantes na chamada">
      {participants.map((participant) => (
        <li
          key={participant.identity}
          className={participant.isSpeaking ? 'voice-roster-item is-speaking' : 'voice-roster-item'}
        >
          <span className="voice-roster-dot" aria-hidden="true" />
          <span className="voice-roster-name">{participant.name || participant.identity}</span>
          {participant.isScreenShareEnabled && (
            <button
              type="button"
              className="voice-roster-live"
              title={`Assistir a tela de ${participant.name || participant.identity}`}
              onClick={() => {
                onFocus(`${participant.identity}:${Track.Source.ScreenShare}`);
              }}
            >
              ● AO VIVO
            </button>
          )}
          {participant.isMicrophoneEnabled ? null : (
            <span className="voice-roster-muted" aria-label="microfone desligado">
              🔇
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Arrivals and departures
// ---------------------------------------------------------------------------

interface VoiceChimesProps {
  /** Whether this session wants to hear them. */
  enabled: boolean;
}

/**
 * Renders nothing: it listens to the room and rings.
 *
 * The two facts a voice channel needs to announce are that somebody arrived
 * and that somebody left — the roster shows both, but only to whoever happens
 * to be looking at it, and a voice channel is the one place a person is
 * deliberately not looking at the screen.
 *
 * LiveKit only raises these two events for *remote* participants, and never
 * for the ones already in the room when we connect, so joining a busy channel
 * is silent and nobody is announced to themselves. `shouldPlayChime` states
 * both rules anyway, because they are ours and the event source is not.
 *
 * The preference is read through a ref rather than closed over: flipping the
 * control must not unsubscribe and resubscribe the room, which is how a chime
 * gets lost in the gap.
 */
function VoiceChimes({ enabled }: VoiceChimesProps): null {
  const room = useRoomContext();
  const enabledRef = useRef(enabled);
  const lastPlayedAt = useRef<number | null>(null);

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  useEffect(() => {
    function announce(kind: VoiceChimeKind) {
      return (participant: RemoteParticipant): void => {
        const now = Date.now();

        if (
          !shouldPlayChime({
            enabled: enabledRef.current,
            isLocal: participant.isLocal,
            now,
            lastPlayedAt: lastPlayedAt.current,
          })
        ) {
          return;
        }

        lastPlayedAt.current = now;
        playVoiceChime(kind);
      };
    }

    const onJoin = announce('join');
    const onLeave = announce('leave');

    room.on(RoomEvent.ParticipantConnected, onJoin);
    room.on(RoomEvent.ParticipantDisconnected, onLeave);

    // LiveKit raises the two above only for *other* people. Announcing your
    // own arrival and departure needs the room's own events — and that is the
    // half that matters when you get dropped: the sound is the only thing that
    // tells you, because a voice channel is used with the screen out of sight.
    const onSelfJoin = (): void => announce('join')({ isLocal: true } as RemoteParticipant);
    const onSelfLeave = (): void => announce('leave')({ isLocal: true } as RemoteParticipant);

    room.on(RoomEvent.Connected, onSelfJoin);
    room.on(RoomEvent.Disconnected, onSelfLeave);

    return () => {
      room.off(RoomEvent.ParticipantConnected, onJoin);
      room.off(RoomEvent.ParticipantDisconnected, onLeave);
      room.off(RoomEvent.Connected, onSelfJoin);
      room.off(RoomEvent.Disconnected, onSelfLeave);
    };
  }, [room]);

  return null;
}

// ---------------------------------------------------------------------------
// The shell's global key (§5.2)
// ---------------------------------------------------------------------------

/**
 * Renders nothing: it hands the shell's key the microphone of this room.
 *
 * It lives inside `LiveKitRoom` because that is where the local participant
 * is, and the state it applies is decided outside — the subscription belongs
 * to the panel, which exists before the room connects and outlives every
 * reconnection.
 *
 * The microphone is asked for what the state says, never toggled: a toggle
 * would drift the moment an event was missed, and a microphone that is open
 * when the page believes it is shut is the failure this whole feature exists
 * to avoid.
 */
function PushToTalkMicrophone({ state }: { state: PushToTalkState }): null {
  const { localParticipant } = useLocalParticipant();
  const { armed, open } = state;

  useEffect(() => {
    // No shell, or a shell with the shortcut switched off: the microphone is
    // whatever the room joined with, exactly as in a browser.
    if (!armed) return;

    void localParticipant.setMicrophoneEnabled(open).catch(() => {
      // The device can be unplugged mid-call. LiveKit reports the failure on
      // its own, and the toggle keeps showing what is really published, which
      // is the honest answer — this only keeps the rejection from escaping.
    });
  }, [armed, open, localParticipant]);

  return null;
}

// ---------------------------------------------------------------------------
// Per-participant volume
// ---------------------------------------------------------------------------

/** Percent as shown on the slider, turned into the gain LiveKit expects. */
export function volumeGain(percent: number): number {
  return Math.min(Math.max(percent, 0), 200) / 100;
}

const DEFAULT_VOLUME_PERCENT = 100;

/**
 * One slider per remote participant.
 *
 * The volume is applied on the subscriber side, so lowering someone here is
 * this session's business and nobody else hears the difference.
 */
function VoiceVolumes(): ReactElement | null {
  const participants = useRemoteParticipants();
  const [percents, setPercents] = useState<Record<string, number>>({});

  if (participants.length === 0) {
    return null;
  }

  return (
    <ul className="voice-volumes">
      {participants.map((participant) => {
        const percent = percents[participant.identity] ?? DEFAULT_VOLUME_PERCENT;
        // The name comes from the token; the identity is the user id, and it
        // only surfaces if a token was ever issued without a display name.
        const label = participant.name || participant.identity;

        return (
          <li key={participant.identity} className="voice-volume">
            <span className="voice-volume-name">{label}</span>
            <input
              type="range"
              min={0}
              max={200}
              step={5}
              value={percent}
              aria-label={`Volume de ${label}`}
              onChange={(event) => {
                const next = event.target.valueAsNumber;
                setPercents((previous) => ({ ...previous, [participant.identity]: next }));
                participant.setVolume(volumeGain(next));
              }}
            />
            <span className="voice-volume-value">{percent}%</span>
          </li>
        );
      })}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Room
// ---------------------------------------------------------------------------

interface VoiceCredentials {
  token: string;
  url: string;
}

export interface VoiceRoomProps {
  channelId: string;
  channelName: string;
  /** Called once the room is left, by the button or by the server. */
  onLeave: () => void;
}

export function VoiceRoom({ channelId, channelName, onLeave }: VoiceRoomProps): ReactElement {
  const [credentials, setCredentials] = useState<VoiceCredentials | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [roomError, setRoomError] = useState<string | null>(null);
  // Decided before the picker opens: with it off, Chrome never offers the
  // "share audio" checkbox, which is the only way to rule the sound out.
  const [shareAudio, setShareAudio] = useState(true);
  const [quality, setQuality] = useState<ScreenShareQuality>('suave');
  // Ninguem entra assistindo; a grade e o estado de repouso.
  const [focusedKey, setFocusedKey] = useState<string | null>(null);
  const [chimes, setChimes] = useState(readChimePreference);
  const [pushToTalk, setPushToTalk] = useState<PushToTalkState>(IDLE_PUSH_TO_TALK);

  /**
   * The shell's global key, subscribed for as long as this panel exists.
   *
   * Here rather than inside `LiveKitRoom` for two reasons: the panel is
   * mounted before the token arrives, so the shell's first word is not missed
   * while the room is still connecting; and leaving the channel unmounts this,
   * which unsubscribes. A listener that outlived the panel would go on opening
   * the microphone of a call that no longer exists.
   *
   * In a browser there is no bridge and this subscribes to nothing.
   */
  useEffect(
    () =>
      subscribeToPushToTalk((talking) => {
        setPushToTalk((state) => nextPushToTalk(state, talking));
      }),
    [],
  );

  /**
   * The token is scoped to one room and expires, so it is fetched per channel
   * and never cached across channels: a token for another channel would be
   * refused by the SFU anyway.
   */
  useEffect(() => {
    let active = true;
    setCredentials(null);
    setError(null);
    setRoomError(null);

    apiFetch<VoiceCredentials>('/api/voice/token', {
      method: 'POST',
      body: { channelId },
    })
      .then((issued) => {
        if (active) {
          setCredentials(issued);
        }
      })
      .catch((cause: unknown) => {
        if (active) {
          setError(cause instanceof ApiError ? cause.message : GENERIC_FAILURE);
        }
      });

    return () => {
      active = false;
    };
  }, [channelId]);

  const header = (
    <header className="voice-panel-header">
      <span className="voice-panel-sigil" aria-hidden="true">
        ♪
      </span>
      <span className="voice-panel-name">{channelName}</span>
    </header>
  );

  if (error !== null) {
    return (
      <section className="voice-panel" aria-label={`Canal de voz ${channelName}`}>
        {header}
        <p className="voice-status" role="alert">
          {error}
        </p>
        <div className="voice-controls">
          <button type="button" className="voice-leave" onClick={onLeave}>
            Sair da chamada
          </button>
        </div>
      </section>
    );
  }

  if (credentials === null) {
    return (
      <section className="voice-panel" aria-label={`Canal de voz ${channelName}`}>
        {header}
        <p className="voice-status">Conectando ao canal de voz…</p>
      </section>
    );
  }

  return (
    <section className="voice-panel" aria-label={`Canal de voz ${channelName}`}>
      {header}

      {/*
        A banner rather than a replacement for the room: unmounting the room to
        show a message would disconnect it, and that disconnect calls `onLeave`,
        which takes this panel away before anyone reads what went wrong.
      */}
      {roomError !== null && (
        <p className="voice-status" role="alert">
          {roomError}
        </p>
      )}

      <LiveKitRoom
        className="voice-room"
        serverUrl={credentials.url}
        token={credentials.token}
        connect={true}
        // With the key in charge the room joins without publishing the
        // microphone at all. §5.2 asks for it to open while the key is held,
        // and joining open to mute a moment later is a moment of being heard
        // by everyone that nobody asked for.
        audio={!pushToTalk.armed}
        video={false}
        options={ROOM_OPTIONS}
        onDisconnected={onLeave}
        onError={() => {
          setRoomError(GENERIC_FAILURE);
        }}
      >
        {/* Plays every remote microphone and every shared tab's audio. */}
        <RoomAudioRenderer />
        <PushToTalkMicrophone state={pushToTalk} />
        <VoiceChimes enabled={chimes} />
        <VoiceStage focusedKey={focusedKey} onFocus={setFocusedKey} />
        <ScreenAudioWarning wanted={shareAudio} />
        <VoiceRoster onFocus={setFocusedKey} />
        <VoiceVolumes />

        <div className="voice-controls">
          <TrackToggle source={Track.Source.Microphone}>Microfone</TrackToggle>
          {/*
            Said out loud beside the toggle, because a key that works with the
            window minimised is a key whose effect nobody is watching. The
            toggle itself shows what the room is really publishing — this
            reads from the same state that was just handed to the microphone,
            so the two cannot tell different stories.
          */}
          {pushToTalk.armed && (
            <p
              className={pushToTalk.open ? 'voice-push-to-talk is-open' : 'voice-push-to-talk'}
              role="status"
            >
              Push-to-talk: {pushToTalk.open ? 'microfone aberto' : 'microfone fechado'}
            </p>
          )}
          <TrackToggle source={Track.Source.Camera}>Câmera</TrackToggle>
          <TrackToggle
            source={Track.Source.ScreenShare}
            captureOptions={screenShareOptions(quality, shareAudio)}
          >
            Compartilhar tela
          </TrackToggle>
          <AudioSettings />
          <label className="voice-share-audio">
            <input
              type="checkbox"
              checked={chimes}
              onChange={(event) => {
                const next = event.target.checked;
                setChimes(next);
                writeChimePreference(next);
              }}
            />
            Sons de entrada e saída
          </label>
          <label className="voice-share-audio">
            <select
              className="voice-quality-select"
              value={quality}
              aria-label="Qualidade da tela compartilhada"
              onChange={(event) => {
                setQuality(event.target.value as ScreenShareQuality);
              }}
            >
              {Object.entries(SCREEN_SHARE_QUALITIES).map(([chave, { rotulo }]) => (
                <option key={chave} value={chave}>
                  {rotulo}
                </option>
              ))}
            </select>
          </label>
          <label className="voice-share-audio">
            <input
              type="checkbox"
              checked={shareAudio}
              onChange={(event) => {
                setShareAudio(event.target.checked);
              }}
            />
            Com som
          </label>
          <DisconnectButton className="voice-leave">Sair da chamada</DisconnectButton>
        </div>
      </LiveKitRoom>
    </section>
  );
}

export default VoiceRoom;
